import type { Salutation } from '@schuelerhilfe/shared/src/enums/salutation';
import type { LocationDto } from '@/lib/api';
import { TimeSlotMode } from '@/lib/appointments';
import type { AppointmentDay } from '@/lib/appointments';

export interface SubmittedSnapshot {
  salutation: Salutation;
  lastname: string;
  date: string;
  time: string;
}

export enum Step {
  /** Standort selection — only reached on pages without a Standort in the render context (e.g. City Page). */
  Location = 'location',
  Day = 'day',
  Time = 'time',
  Form = 'form',
}

export enum SlotsStatus {
  Loading = 'loading',
  Idle = 'idle',
  Fallback = 'fallback',
}

export enum LocationStatus {
  Idle = 'idle',
  /** The full Standort is being fetched after the visitor picked a search result. */
  Loading = 'loading',
  /** The fetch failed — the visitor stays on the location step and can retry. */
  Error = 'error',
}

export type BookingState = {
  slots: AppointmentDay[];
  slotsStatus: SlotsStatus;
  step: Step;
  selectedDay: string | null;
  selectedTime: string | null;
  selectedMode: TimeSlotMode | null;
  submitted: SubmittedSnapshot | null;
  /**
   * Standort the visitor picked in the widget's own location step. Stays
   * null when the page already provides one (Location Page).
   */
  selectedLocation: LocationDto | null;
  locationStatus: LocationStatus;
};

export type BookingAction =
  | { type: 'SLOTS_LOADED'; slots: AppointmentDay[] }
  | { type: 'SLOTS_FALLBACK' }
  | { type: 'DAY_SELECTED'; dateIso: string }
  | { type: 'TIME_SELECTED'; time: string; mode: TimeSlotMode }
  | { type: 'STEP_SET'; step: Step }
  | { type: 'SUBMIT_SUCCESS'; snapshot: SubmittedSnapshot }
  | { type: 'LOCATION_LOADING' }
  | { type: 'LOCATION_SELECTED'; location: LocationDto }
  | { type: 'LOCATION_FAILED' }
  | { type: 'LOCATION_CLEARED' };

const NO_SELECTION = {
  selectedDay: null,
  selectedTime: null,
  selectedMode: null,
} as const;

export function bookingReducer(
  state: BookingState,
  action: BookingAction,
): BookingState {
  switch (action.type) {
    case 'SLOTS_LOADED':
      return { ...state, slots: action.slots, slotsStatus: SlotsStatus.Idle };
    case 'SLOTS_FALLBACK':
      return { ...state, slotsStatus: SlotsStatus.Fallback };
    case 'DAY_SELECTED':
      return {
        ...state,
        selectedDay: action.dateIso,
        selectedTime: null,
        selectedMode: null,
      };
    case 'TIME_SELECTED':
      return { ...state, selectedTime: action.time, selectedMode: action.mode };
    case 'STEP_SET':
      if (action.step === Step.Day) {
        return {
          ...state,
          step: Step.Day,
          selectedTime: null,
          selectedMode: null,
        };
      }
      return { ...state, step: action.step };
    case 'SUBMIT_SUCCESS':
      return { ...state, submitted: action.snapshot };
    case 'LOCATION_LOADING':
      return { ...state, locationStatus: LocationStatus.Loading };
    case 'LOCATION_SELECTED':
      // A new Standort means new slots: drop everything chosen so far and
      // start loading — the widget's slots effect keys on the slug.
      return {
        ...state,
        ...NO_SELECTION,
        selectedLocation: action.location,
        locationStatus: LocationStatus.Idle,
        step: Step.Day,
        slots: [],
        slotsStatus: SlotsStatus.Loading,
      };
    case 'LOCATION_FAILED':
      return { ...state, locationStatus: LocationStatus.Error };
    case 'LOCATION_CLEARED':
      return {
        ...state,
        ...NO_SELECTION,
        selectedLocation: null,
        locationStatus: LocationStatus.Idle,
        step: Step.Location,
        slots: [],
        slotsStatus: SlotsStatus.Idle,
      };
  }
}

export interface InitialBookingStateOptions {
  /**
   * Without a slug: open on the location step (the visitor picks a Standort
   * first) instead of the legacy fallback slots. The modal widgets keep the
   * default — they never render a location step.
   */
  withLocationStep?: boolean;
}

export function initialBookingState(
  slug: string | undefined,
  options: InitialBookingStateOptions = {},
): BookingState {
  const selectLocation = !slug && Boolean(options.withLocationStep);
  return {
    slots: [],
    slotsStatus: slug
      ? SlotsStatus.Loading
      : selectLocation
        ? SlotsStatus.Idle
        : SlotsStatus.Fallback,
    step: selectLocation ? Step.Location : Step.Day,
    ...NO_SELECTION,
    submitted: null,
    selectedLocation: null,
    locationStatus: LocationStatus.Idle,
  };
}
